/**
 * Returns true if the JWT is expired or malformed.
 * Used in route `beforeLoad` guards to avoid redirect loops between
 * the login page and the dashboard auth guard.
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  return typeof payload.exp === 'number' && payload.exp * 1000 < Date.now();
}

/**
 * Best-effort JWT payload decode. Returns null for malformed tokens. We do
 * NOT verify the signature here — the API does that on every call. Used by
 * the impersonation handoff page to surface readable claims to the UI.
 *
 * `atob` handles the standard alphabet; JWTs use base64url, so swap the
 * URL-safe characters back and pad before decoding. The Unicode round-trip
 * via `escape`/`decodeURIComponent` covers email claims that contain
 * non-ASCII characters.
 */
export function decodeJwtPayload<T = Record<string, unknown>>(token: string): T | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const b64url = parts[1]!;
    const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const decoded = atob(padded);
    // Re-decode as UTF-8 — atob returns latin-1 bytes.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const json = decodeURIComponent(escape(decoded));
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

/**
 * Claims we expect on an impersonation JWT. The provisioner mints these via
 * the operations backend's `/admin/impersonation/start` endpoint.
 */
export interface ImpersonationJwtClaims {
  sub: string; // impersonated userId
  tenantId: string;
  impersonation: true;
  impersonatedBy: string; // admin email
  impersonationId: string;
  exp: number; // seconds since epoch
  iat?: number;
  email?: string; // impersonated user's email, when available
  roles?: string[];
  permissions?: string[];
  permissionsVersion?: number;
}

export function decodeImpersonationToken(token: string): ImpersonationJwtClaims | null {
  const payload = decodeJwtPayload<Partial<ImpersonationJwtClaims>>(token);
  if (!payload) return null;
  if (payload.impersonation !== true) return null;
  if (typeof payload.sub !== 'string' || !payload.sub) return null;
  if (typeof payload.tenantId !== 'string' || !payload.tenantId) return null;
  if (typeof payload.impersonatedBy !== 'string' || !payload.impersonatedBy) return null;
  if (typeof payload.impersonationId !== 'string' || !payload.impersonationId) return null;
  if (typeof payload.exp !== 'number') return null;
  return payload as ImpersonationJwtClaims;
}
